import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import api from '../services/apiService';
import { UserContext } from '../contexts/UserContext';
import EmailTemplateEditor from './EmailTemplateEditor';
import PPEEmailTemplateEditor from './PPEEmailTemplateEditor';
import WarehouseIncomingDeliveries from './WarehouseIncomingDeliveries';
import CooperatingCompanies from './CooperatingCompanies';
import SupportTicketsPanel from './SupportTicketsPanel';

const Settings = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  
  // Subscription state
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [availableSubscriptionTypes, setAvailableSubscriptionTypes] = useState([]);
  
  // Determine which tabs to show based on subscriptions
  const hasAnySubscription = () => {
    return subscriptionData?.has_any_subscription || false;
  };
  
  const hasSubscription = (subscriptionKey) => {
    return subscriptionData?.subscription_checks?.[subscriptionKey] || false;
  };
  
  // Set default active tab based on subscription status
  const getDefaultTab = () => {
    if (!subscriptionData) return 'subscriptions';
    
    if (!hasAnySubscription()) {
      return 'subscriptions'; // Only subscriptions tab when no subscriptions
    }
    
    // Priority order: General -> Subscriptions -> PPE
    if (hasAnySubscriptionWithGeneral()) {
      return 'general';
    }
    
    if (hasSubscription('ppe')) { // Type 4
      return 'ppe';
    }
    
    return 'subscriptions';
  };
  
  // Check if user has any subscription that enables General tab
  const hasAnySubscriptionWithGeneral = () => {
    return hasSubscription('nominations') ||    // Type 1
           hasSubscription('work_permits') ||   // Type 2
           hasSubscription('trainings') ||      // Type 3
           hasSubscription('ppe');              // Type 4
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  
  // PPE subcategory state
  const [activePpeSubTab, setActivePpeSubTab] = useState('ppe_settings');
  
  // Default PPE Settings (colors as hex values)
  const defaultPpeSettings = {
    // Inspection settings
    ppe_inspection_expired_color: '#FEE2E2',  // red-100
    ppe_inspection_first_check: 30,
    ppe_inspection_first_check_color: '#FFEDD5',  // orange-100
    ppe_inspection_second_check: 60,
    ppe_inspection_second_check_color: '#FEF9C3',  // yellow-100

    // Manufacturer settings
    ppe_manufacturer_expired_color: '#FEE2E2',  // red-100
    ppe_manufacturer_first_check: 30,
    ppe_manufacturer_first_check_color: '#FFEDD5',  // orange-100
    ppe_manufacturer_second_check: 60,
    ppe_manufacturer_second_check_color: '#FEF9C3'  // yellow-100
  };

  // PPE Settings State
  const [ppeSettings, setPpeSettings] = useState(defaultPpeSettings);
  
  // Service Companies State
  const [serviceCompanies, setServiceCompanies] = useState([]);
  const [showServiceCompanyModal, setShowServiceCompanyModal] = useState(false);
  const [editingServiceCompany, setEditingServiceCompany] = useState(null);
  const [serviceCompanyForm, setServiceCompanyForm] = useState({
    name: '',
    address: '',
    postal_code: '',
    phone: '',
    email: '',
    contact_person: ''
  });

  // Service Company Templates State
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedTemplateCompany, setSelectedTemplateCompany] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    service_company: '',
    template_type: 'pdf',
    send_email: false,
    email_address: '',
    email_subject: '',
    email_body: '',
    email_template_html: '',
    include_equipment_table: true,
    equipment_fields: [
      'safety_equipment_type__name',
      'safety_equipment_serial_number',
      'safety_equipment_status',
      'safety_equipment_model__name'
    ],
    table_config: {
      show_headers: true,
      border_style: 'solid',
      border_color: '#000000',
      header_bg_color: '#f3f4f6'
    },
    custom_variables: {},
    generate_pdf: true,
    pdf_template_name: '',
    company_logo: null,
    service_company_logo: null,
    header_text: '',
    footer_text: '',
    pdf_layout_config: null,
    custom_fields: null
  });

  // General Settings State
  const [generalSettings, setGeneralSettings] = useState({
    company_logo: null,
    company_logo_preview: null
  });

  
  // Load current user settings and subscription data
  useEffect(() => {
    if (user.id) {
      loadSubscriptionData(); // Load subscription data first
      loadUserSettings();
      loadGeneralSettings();
    }
  }, [user.id]);
  
  // Update active tab when subscription data changes
  useEffect(() => {
    if (subscriptionData && !subscriptionLoading) {
      const defaultTab = getDefaultTab();
      setActiveTab(defaultTab);
    }
  }, [subscriptionData, subscriptionLoading]);
  
  // Load service companies and templates after subscription data is loaded
  useEffect(() => {
    if (subscriptionData && !subscriptionLoading) {
      loadServiceCompanies();
      loadServiceTemplates();
    }
  }, [subscriptionData, subscriptionLoading]);

  const loadUserSettings = async () => {
    setLoading(true);
    try {
      // Get detailed user data including PPE settings
      const response = await api.get(`/users/${user.id}/`);
      const userData = response.data;
      
      // Extract PPE settings from user data
      const userPpeSettings = {
        ppe_inspection_expired_color: userData.ppe_inspection_expired_color || defaultPpeSettings.ppe_inspection_expired_color,
        ppe_inspection_first_check: userData.ppe_inspection_first_check || defaultPpeSettings.ppe_inspection_first_check,
        ppe_inspection_first_check_color: userData.ppe_inspection_first_check_color || defaultPpeSettings.ppe_inspection_first_check_color,
        ppe_inspection_second_check: userData.ppe_inspection_second_check || defaultPpeSettings.ppe_inspection_second_check,
        ppe_inspection_second_check_color: userData.ppe_inspection_second_check_color || defaultPpeSettings.ppe_inspection_second_check_color,
        ppe_manufacturer_expired_color: userData.ppe_manufacturer_expired_color || defaultPpeSettings.ppe_manufacturer_expired_color,
        ppe_manufacturer_first_check: userData.ppe_manufacturer_first_check || defaultPpeSettings.ppe_manufacturer_first_check,
        ppe_manufacturer_first_check_color: userData.ppe_manufacturer_first_check_color || defaultPpeSettings.ppe_manufacturer_first_check_color,
        ppe_manufacturer_second_check: userData.ppe_manufacturer_second_check || defaultPpeSettings.ppe_manufacturer_second_check,
        ppe_manufacturer_second_check_color: userData.ppe_manufacturer_second_check_color || defaultPpeSettings.ppe_manufacturer_second_check_color,
      };
      
      setPpeSettings(userPpeSettings);
    } catch (error) {
      console.error('Error loading user settings:', error);
      toast.error('Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePpeSettingChange = (field, value) => {
    setPpeSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const savePpeSettings = async () => {
    setSaving(true);
    try {
      // Update user PPE settings
      await api.patch(`/users/${user.id}/`, ppeSettings);
      toast.success('PPE settings saved successfully');
    } catch (error) {
      console.error('Error saving PPE settings:', error);
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const resetPpeSettings = () => {
    setPpeSettings(defaultPpeSettings);
    toast.info('PPE settings reset to default values');
  };

  // Service Companies functions
  const loadServiceCompanies = async () => {
    if (!hasSubscription('ppe')) return;
    
    try {
      const response = await api.get('/servicecompany/');
      setServiceCompanies(response.data);
    } catch (error) {
      console.error('Error loading service companies:', error);
      toast.error('Error loading service companies');
    }
  };

  const openServiceCompanyModal = (company = null) => {
    if (company) {
      setEditingServiceCompany(company);
      setServiceCompanyForm(company);
    } else {
      setEditingServiceCompany(null);
      setServiceCompanyForm({
        name: '',
        address: '',
        postal_code: '',
        phone: '',
        email: '',
        contact_person: ''
      });
    }
    setShowServiceCompanyModal(true);
  };

  const closeServiceCompanyModal = () => {
    setShowServiceCompanyModal(false);
    setEditingServiceCompany(null);
    setServiceCompanyForm({
      name: '',
      address: '',
      postal_code: '',
      phone: '',
      email: '',
      contact_person: ''
    });
  };

  const handleServiceCompanyFormChange = (field, value) => {
    setServiceCompanyForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveServiceCompany = async () => {
    if (!serviceCompanyForm.name.trim() || !serviceCompanyForm.address.trim()) {
      toast.error('Name and address are required');
      return;
    }

    setSaving(true);
    try {
      if (editingServiceCompany) {
        // Update existing
        await api.patch(`/servicecompany/${editingServiceCompany.id}/`, serviceCompanyForm);
        toast.success('Service company updated successfully');
      } else {
        // Create new
        await api.post('/servicecompany/', serviceCompanyForm);
        toast.success('Service company created successfully');
      }
      
      closeServiceCompanyModal();
      loadServiceCompanies();
    } catch (error) {
      console.error('Error saving service company:', error);
      toast.error('Error saving service company');
    } finally {
      setSaving(false);
    }
  };

  const deleteServiceCompany = async (company) => {
    if (!window.confirm(`Are you sure you want to delete "${company.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/servicecompany/${company.id}/`);
      toast.success('Service company deleted successfully');
      loadServiceCompanies();
    } catch (error) {
      console.error('Error deleting service company:', error);
      toast.error('Error deleting service company');
    }
  };

  // Service Company Templates functions
  const loadServiceTemplates = async () => {
    if (!hasSubscription('ppe')) return;
    
    try {
      const response = await api.get('/service-templates/');
      setServiceTemplates(response.data);
    } catch (error) {
      console.error('Error loading service templates:', error);
      toast.error('Error loading service templates');
    }
  };

  const openTemplateModal = (template = null, company = null) => {
    setEditingTemplate(template);
    setSelectedTemplateCompany(company);
    if (template) {
      setTemplateForm({
        service_company: template.service_company,
        template_type: template.template_type,
        send_email: template.send_email || false,
        email_address: template.email_address || '',
        email_subject: template.email_subject || '',
        email_body: template.email_body || '',
        email_template_html: template.email_template_html || '',
        include_equipment_table: template.include_equipment_table ?? true,
        equipment_fields: template.equipment_fields || [
          'safety_equipment_type__name',
          'safety_equipment_serial_number',
          'safety_equipment_status',
          'safety_equipment_model__name'
        ],
        table_config: template.table_config || {
          show_headers: true,
          border_style: 'solid',
          border_color: '#000000',
          header_bg_color: '#f3f4f6'
        },
        custom_variables: template.custom_variables || {},
        generate_pdf: template.generate_pdf || false,
        pdf_template_name: template.pdf_template_name || '',
        company_logo: template.company_logo,
        service_company_logo: template.service_company_logo,
        header_text: template.header_text || '',
        footer_text: template.footer_text || '',
        pdf_layout_config: template.pdf_layout_config,
        custom_fields: template.custom_fields
      });
    } else {
      setTemplateForm({
        service_company: company?.id || '',
        template_type: 'pdf',
        send_email: false,
        email_address: '',
        email_subject: 'Equipment Service Request',
        email_body: '',
        email_template_html: '<h2>Equipment Service Request</h2><p>Dear service team,</p><p>We are sending the following equipment for service:</p>{{EQUIPMENT_TABLE}}<p>Best regards,<br>{{COMPANY_NAME}}</p>',
        include_equipment_table: true,
        equipment_fields: [
          'safety_equipment_type__name',
          'safety_equipment_serial_number',
          'safety_equipment_status',
          'safety_equipment_model__name'
        ],
        table_config: {
          show_headers: true,
          border_style: 'solid',
          border_color: '#000000',
          header_bg_color: '#f3f4f6'
        },
        custom_variables: {
          'COMPANY_NAME': 'Your Company Name',
          'CONTACT_EMAIL': 'contact@company.com'
        },
        generate_pdf: true,
        pdf_template_name: company?.name ? `${company.name} Template` : '',
        company_logo: null,
        service_company_logo: null,
        header_text: 'DELIVERY NOTE',
        footer_text: 'Generated by ventro.energy',
        pdf_layout_config: null,
        custom_fields: null
      });
    }
    setShowTemplateModal(true);
  };

  const closeTemplateModal = () => {
    setShowTemplateModal(false);
    setEditingTemplate(null);
    setSelectedTemplateCompany(null);
    setTemplateForm({
      service_company: '',
      template_type: 'pdf',
      send_email: false,
      email_address: '',
      email_subject: '',
      email_body: '',
      email_template_html: '',
      include_equipment_table: true,
      equipment_fields: [
        'safety_equipment_type__name',
        'safety_equipment_serial_number',
        'safety_equipment_status',
        'safety_equipment_model__name'
      ],
      table_config: {
        show_headers: true,
        border_style: 'solid',
        border_color: '#000000',
        header_bg_color: '#f3f4f6'
      },
      custom_variables: {},
      generate_pdf: true,
      pdf_template_name: '',
      company_logo: null,
      service_company_logo: null,
      header_text: '',
      footer_text: '',
      pdf_layout_config: null,
      custom_fields: null
    });
  };

  const saveServiceTemplate = async (e) => {
    e.preventDefault();
    
    if (!templateForm.service_company) {
      toast.error('Please select a service company');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      
      // Add all form fields
      Object.keys(templateForm).forEach(key => {
        if (templateForm[key] !== null && templateForm[key] !== undefined) {
          if (key === 'pdf_layout_config' || key === 'custom_fields' || 
              key === 'equipment_fields' || key === 'table_config' || 
              key === 'custom_variables') {
            if (templateForm[key]) {
              formData.append(key, JSON.stringify(templateForm[key]));
            }
          } else if (key === 'company_logo' || key === 'service_company_logo') {
            if (templateForm[key] instanceof File) {
              formData.append(key, templateForm[key]);
            }
          } else {
            formData.append(key, templateForm[key]);
          }
        }
      });

      if (editingTemplate) {
        await api.patch(`/service-templates/${editingTemplate.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Template updated successfully');
      } else {
        await api.post('/service-templates/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Template created successfully');
      }
      
      closeTemplateModal();
      loadServiceTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error saving template');
    } finally {
      setSaving(false);
    }
  };

  const deleteServiceTemplate = async (template) => {
    if (!window.confirm(`Are you sure you want to delete template for "${template.service_company_name}"?`)) {
      return;
    }

    try {
      await api.delete(`/service-templates/${template.id}/`);
      toast.success('Template deleted successfully');
      loadServiceTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error deleting template');
    }
  };

  // General Settings functions
  const loadGeneralSettings = async () => {
    if (!user?.company) return;
    
    try {
      const response = await api.get('/company/settings/');
      const companyData = response.data;
      
      setGeneralSettings({
        company_logo: companyData.company_logo,
        company_logo_preview: companyData.company_logo
      });
    } catch (error) {
      console.error('Error loading company settings:', error);
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setGeneralSettings(prev => ({
        ...prev,
        company_logo: file,
        company_logo_preview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeCompanyLogo = () => {
    setGeneralSettings({
      company_logo: null,
      company_logo_preview: null
    });
  };

  const saveGeneralSettings = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      
      if (generalSettings.company_logo instanceof File) {
        formData.append('company_logo', generalSettings.company_logo);
      } else if (generalSettings.company_logo === null) {
        // If logo was removed, send empty value
        formData.append('company_logo', '');
      }

      await api.patch('/company/settings/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('General settings saved successfully');
      await loadGeneralSettings(); // Refresh data
    } catch (error) {
      console.error('Error saving general settings:', error);
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const loadSubscriptionData = async () => {
    setSubscriptionLoading(true);
    try {
      // Load company subscription status
      const statusResponse = await api.get('/company/subscription-status/');
      setSubscriptionData(statusResponse.data);

      // Load available subscription types
      const typesResponse = await api.get('/subscription-types/');
      setAvailableSubscriptionTypes(typesResponse.data);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toast.error('Error loading subscription information');
      // Set fallback data so UI doesn't break
      setSubscriptionData({
        has_any_subscription: false,
        subscription_checks: {}
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const renderColorSelector = (field, value) => {
    return (
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value || '#FEE2E2'}
          onChange={(e) => handlePpeSettingChange(field, e.target.value)}
          className="h-10 w-20 p-1 border border-gray-300 rounded-md cursor-pointer"
        />
        <div className="flex items-center gap-2">
          <div
            className="h-6 w-6 rounded-full border border-gray-300"
            style={{ backgroundColor: value || '#FEE2E2' }}
          />
          <span className="text-sm text-gray-600">{value || '#FEE2E2'}</span>
        </div>
      </div>
    );
  };

  const renderPpeTab = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* PPE Subcategory Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" aria-label="PPE Subcategories">
            <button
              onClick={() => setActivePpeSubTab('ppe_settings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activePpeSubTab === 'ppe_settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              PPE Settings
            </button>
            <button
              onClick={() => setActivePpeSubTab('service_companies')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activePpeSubTab === 'service_companies'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Service Companies
            </button>
            <button
              onClick={() => setActivePpeSubTab('email_templates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activePpeSubTab === 'email_templates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              PPE Email Templates
            </button>
{user?.company_can_inspection && (
              <button
                onClick={() => setActivePpeSubTab('cooperating_companies')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activePpeSubTab === 'cooperating_companies'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Cooperating Companies
              </button>
            )}
          </nav>
        </div>

        {/* PPE Settings Content */}
        {activePpeSubTab === 'ppe_settings' && (
          <div className="space-y-8">
            {/* Reset Button */}
            <div className="flex justify-between items-center pb-4 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">PPE Settings</h2>
                <p className="text-sm text-gray-600">Configure thresholds and colors for PPE warnings</p>
              </div>
              <button
                onClick={resetPpeSettings}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                Reset to Defaults
              </button>
            </div>

            {/* Inspection Settings */}
            <div>
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
                PPE Safety Inspection Settings
              </h3>
              
              <div className="space-y-6">
                {/* Expired Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color for expired inspections
                  </label>
                  {renderColorSelector('ppe_inspection_expired_color', ppeSettings.ppe_inspection_expired_color)}
                </div>

                {/* First Threshold */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First warning threshold (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={ppeSettings.ppe_inspection_first_check}
                      onChange={(e) => handlePpeSettingChange('ppe_inspection_first_check', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Warning will appear when less than {ppeSettings.ppe_inspection_first_check} days remain
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First threshold color
                    </label>
                    {renderColorSelector('ppe_inspection_first_check_color', ppeSettings.ppe_inspection_first_check_color)}
                  </div>
                </div>

                {/* Second Threshold */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Second warning threshold (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={ppeSettings.ppe_inspection_second_check}
                      onChange={(e) => handlePpeSettingChange('ppe_inspection_second_check', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Warning will appear when less than {ppeSettings.ppe_inspection_second_check} days remain
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Second threshold color
                    </label>
                    {renderColorSelector('ppe_inspection_second_check_color', ppeSettings.ppe_inspection_second_check_color)}
                  </div>
                </div>
              </div>
            </div>

            {/* Manufacturer Settings */}
            <div>
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
                PPE Manufacturer Validity Settings
              </h3>
              
              <div className="space-y-6">
                {/* Expired Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color for expired manufacturer validity
                  </label>
                  {renderColorSelector('ppe_manufacturer_expired_color', ppeSettings.ppe_manufacturer_expired_color)}
                </div>

                {/* First Threshold */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First warning threshold (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={ppeSettings.ppe_manufacturer_first_check}
                      onChange={(e) => handlePpeSettingChange('ppe_manufacturer_first_check', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Warning will appear when less than {ppeSettings.ppe_manufacturer_first_check} days remain
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First threshold color
                    </label>
                    {renderColorSelector('ppe_manufacturer_first_check_color', ppeSettings.ppe_manufacturer_first_check_color)}
                  </div>
                </div>

                {/* Second Threshold */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Second warning threshold (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={ppeSettings.ppe_manufacturer_second_check}
                      onChange={(e) => handlePpeSettingChange('ppe_manufacturer_second_check', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Warning will appear when less than {ppeSettings.ppe_manufacturer_second_check} days remain
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Second threshold color
                    </label>
                    {renderColorSelector('ppe_manufacturer_second_check_color', ppeSettings.ppe_manufacturer_second_check_color)}
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-6 border-t">
              <button
                onClick={savePpeSettings}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Save PPE Settings'}
              </button>
            </div>
          </div>
        )}

        {/* Service Companies Content */}
        {activePpeSubTab === 'service_companies' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center pb-4 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Service Companies</h2>
                <p className="text-sm text-gray-600">Manage companies that can service your PPE equipment</p>
              </div>
              <button
                onClick={() => openServiceCompanyModal()}
                className="px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <span>+</span>
                <span>Add Company</span>
              </button>
            </div>

            {/* Service Companies List */}
            <div className="space-y-4">
              {serviceCompanies.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-4">No service companies configured</p>
                  <button
                    onClick={() => openServiceCompanyModal()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Add First Company
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {serviceCompanies.map((company) => (
                    <div key={company.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-lg text-gray-900">{company.name}</h4>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openTemplateModal(null, company)}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                          >
                            Template
                          </button>
                          <button
                            onClick={() => openServiceCompanyModal(company)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteServiceCompany(company)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Address:</span>
                          <p className="text-gray-900">{company.address}</p>
                          {company.postal_code && (
                            <p className="text-gray-600">{company.postal_code}</p>
                          )}
                        </div>
                        
                        {company.phone && (
                          <div>
                            <span className="text-gray-500">Phone:</span>
                            <p className="text-gray-900">{company.phone}</p>
                          </div>
                        )}
                        
                        {company.email && (
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <p className="text-gray-900">{company.email}</p>
                          </div>
                        )}
                        
                        {company.contact_person && (
                          <div>
                            <span className="text-gray-500">Contact Person:</span>
                            <p className="text-gray-900">{company.contact_person}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* PPE Email Templates Content */}
        {activePpeSubTab === 'email_templates' && (
          <div className="space-y-6">
            <div className="pb-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">PPE Email Templates</h2>
              <p className="text-sm text-gray-600">Configure email templates for PPE notifications sent to users about inspection and manufacturer validity expiry warnings.</p>
            </div>

            <PPEEmailTemplateEditor 
              onTemplateChange={() => {
                // Refresh templates or handle template changes
                toast.success('PPE email templates updated successfully');
              }}
            />
          </div>
        )}

        {/* Cooperating Companies Content */}
        {activePpeSubTab === 'cooperating_companies' && user?.company_can_inspection && (
          <div className="space-y-6">
            <CooperatingCompanies />
          </div>
        )}

        {/* Service Company Modal */}
        {showServiceCompanyModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeServiceCompanyModal} />
              
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={closeServiceCompanyModal}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                      {editingServiceCompany ? 'Edit Service Company' : 'Add Service Company'}
                    </h3>
                    
                    <form onSubmit={(e) => { e.preventDefault(); saveServiceCompany(); }} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Company Name *
                        </label>
                        <input
                          type="text"
                          value={serviceCompanyForm.name}
                          onChange={(e) => handleServiceCompanyFormChange('name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Address *
                        </label>
                        <textarea
                          value={serviceCompanyForm.address}
                          onChange={(e) => handleServiceCompanyFormChange('address', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          value={serviceCompanyForm.postal_code}
                          onChange={(e) => handleServiceCompanyFormChange('postal_code', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={serviceCompanyForm.phone}
                          onChange={(e) => handleServiceCompanyFormChange('phone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={serviceCompanyForm.email}
                          onChange={(e) => handleServiceCompanyFormChange('email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Person
                        </label>
                        <input
                          type="text"
                          value={serviceCompanyForm.contact_person}
                          onChange={(e) => handleServiceCompanyFormChange('contact_person', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={closeServiceCompanyModal}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          disabled={saving}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : (editingServiceCompany ? 'Update' : 'Create')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Service Template Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeTemplateModal} />
              
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={closeTemplateModal}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                      {editingTemplate ? 'Edit Service Template' : 'Create Service Template'}
                      {selectedTemplateCompany && (
                        <span className="text-gray-600 font-normal"> - {selectedTemplateCompany.name}</span>
                      )}
                    </h3>
                    
                    <form onSubmit={saveServiceTemplate} className="space-y-6">
                      {/* Service Company Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Service Company *
                        </label>
                        <select
                          value={templateForm.service_company}
                          onChange={(e) => setTemplateForm({...templateForm, service_company: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                          disabled={!!selectedTemplateCompany}
                        >
                          <option value="">Select service company...</option>
                          {serviceCompanies.map(company => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Template Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Template Type *
                        </label>
                        <div className="flex space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="email"
                              checked={templateForm.template_type === 'email'}
                              onChange={(e) => setTemplateForm({...templateForm, template_type: e.target.value})}
                              className="mr-2"
                            />
                            Email Template
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="pdf"
                              checked={templateForm.template_type === 'pdf'}
                              onChange={(e) => setTemplateForm({...templateForm, template_type: e.target.value})}
                              className="mr-2"
                            />
                            PDF Template
                          </label>
                        </div>
                      </div>

                      {/* Email Settings */}
                      {templateForm.template_type === 'email' && (
                        <div className="space-y-4 border p-4 rounded-lg bg-blue-50">
                          <h4 className="font-medium text-gray-900">Email Configuration</h4>
                          
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={templateForm.send_email}
                              onChange={(e) => setTemplateForm({...templateForm, send_email: e.target.checked})}
                              className="mr-2"
                            />
                            Send email to service company
                          </label>

                          {templateForm.send_email && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Email Address *
                                </label>
                                <input
                                  type="email"
                                  value={templateForm.email_address}
                                  onChange={(e) => setTemplateForm({...templateForm, email_address: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                  required
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Email Subject
                                </label>
                                <input
                                  type="text"
                                  value={templateForm.email_subject}
                                  onChange={(e) => setTemplateForm({...templateForm, email_subject: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Equipment Service Request - {{EQUIPMENT_TYPE}}"
                                />
                              </div>

                              {/* Advanced Email Template Editor */}
                              <div className="mt-4">
                                <EmailTemplateEditor
                                  templateHtml={templateForm.email_template_html}
                                  onTemplateChange={(html) => setTemplateForm({...templateForm, email_template_html: html})}
                                  equipmentFields={templateForm.equipment_fields}
                                  onEquipmentFieldsChange={(fields) => setTemplateForm({...templateForm, equipment_fields: fields})}
                                  includeEquipmentTable={templateForm.include_equipment_table}
                                  onIncludeEquipmentTableChange={(include) => setTemplateForm({...templateForm, include_equipment_table: include})}
                                  tableConfig={templateForm.table_config}
                                  onTableConfigChange={(config) => setTemplateForm({...templateForm, table_config: config})}
                                  customVariables={templateForm.custom_variables}
                                  onCustomVariablesChange={(vars) => setTemplateForm({...templateForm, custom_variables: vars})}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* PDF Settings */}
                      {templateForm.template_type === 'pdf' && (
                        <div className="space-y-4 border p-4 rounded-lg bg-green-50">
                          <h4 className="font-medium text-gray-900">PDF Configuration</h4>
                          
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={templateForm.generate_pdf}
                              onChange={(e) => setTemplateForm({...templateForm, generate_pdf: e.target.checked})}
                              className="mr-2"
                            />
                            Generate PDF for download
                          </label>

                          {templateForm.generate_pdf && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  PDF Template Name
                                </label>
                                <input
                                  type="text"
                                  value={templateForm.pdf_template_name}
                                  onChange={(e) => setTemplateForm({...templateForm, pdf_template_name: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Delivery Note Template"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Header Text
                                </label>
                                <textarea
                                  value={templateForm.header_text}
                                  onChange={(e) => setTemplateForm({...templateForm, header_text: e.target.value})}
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="DELIVERY NOTE"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Footer Text
                                </label>
                                <input
                                  type="text"
                                  value={templateForm.footer_text}
                                  onChange={(e) => setTemplateForm({...templateForm, footer_text: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Generated by ventro.energy"
                                />
                              </div>

                              {/* Logo Uploads */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Your Company Logo
                                  </label>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setTemplateForm({...templateForm, company_logo: e.target.files[0]})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Service Company Logo
                                  </label>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setTemplateForm({...templateForm, service_company_logo: e.target.files[0]})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={closeTemplateModal}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          disabled={saving}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
    );
  };

  const renderSubscriptionsTab = () => {
    if (subscriptionLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!subscriptionData) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No subscription data available</p>
          <button
            onClick={loadSubscriptionData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Reload
          </button>
        </div>
      );
    }

    const getStatusColor = (hasSubscription) => {
      return hasSubscription ? 'text-green-600' : 'text-red-600';
    };

    const getStatusIcon = (hasSubscription) => {
      return hasSubscription ? '✓' : '✗';
    };

    // Build a map of module type_id -> expiration info from active subscriptions
    const getModuleExpirationInfo = (typeId) => {
      if (!subscriptionData.active_subscriptions) return null;

      for (const subscription of subscriptionData.active_subscriptions) {
        const hasModule = subscription.subscription_types?.some(t => t.type_id === typeId);
        if (hasModule && subscription.is_active_status) {
          return {
            expirationDate: subscription.expiration_date,
            daysRemaining: subscription.days_remaining
          };
        }
      }
      return null;
    };

    // Map type_id to subscription_checks keys
    const typeIdToCheckKey = {
      1: 'nominations',
      2: 'work_permits',
      3: 'trainings',
      4: 'ppe',
      5: 'warehouse',
      6: 'timesheets',
      7: 'hse',
      8: 'planning_board',
      9: 'cars_equipment'
    };

    return (
      <div>
        <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
          Subscription Modules for company: {subscriptionData.company_name} (id {subscriptionData.company_id})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableSubscriptionTypes.map((subscriptionType) => {
            const checkKey = typeIdToCheckKey[subscriptionType.type_id];
            const isActive = subscriptionData.subscription_checks?.[checkKey] || false;
            const expirationInfo = isActive ? getModuleExpirationInfo(subscriptionType.type_id) : null;

            return (
              <div
                key={subscriptionType.type_id}
                className={`border rounded-lg p-4 ${isActive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">{subscriptionType.name}</h4>
                  <span className={`text-lg ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
                    {isActive ? '✓' : '✗'}
                  </span>
                </div>

                {isActive && expirationInfo?.expirationDate && (
                  <div className="text-sm">
                    <span className="text-gray-500">Valid until: </span>
                    <span className={`font-medium ${expirationInfo.daysRemaining <= 30 ? 'text-orange-600' : 'text-gray-700'}`}>
                      {new Date(expirationInfo.expirationDate).toLocaleDateString()}
                    </span>
                    {expirationInfo.daysRemaining !== null && expirationInfo.daysRemaining <= 30 && (
                      <span className="text-orange-600 text-xs ml-1">
                        ({expirationInfo.daysRemaining} days left)
                      </span>
                    )}
                  </div>
                )}

                {!isActive && (
                  <p className="text-sm text-gray-500">Not subscribed</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <h1 className="text-2xl font-semibold">Settings</h1>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {/* General Tab - Type 1, 2, 3, or 4 */}
            {hasAnySubscriptionWithGeneral() && (
              <button
                onClick={() => setActiveTab('general')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'general'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                General
              </button>
            )}
            
            {/* Subscriptions Tab - always visible */}
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'subscriptions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Subscriptions
            </button>

            {/* PPE Tab - Type 2 (PPE Management) only */}
            {hasSubscription('ppe') && (
              <button
                onClick={() => setActiveTab('ppe')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'ppe'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                PPE
              </button>
            )}

            {/* Support Tickets Tab - always visible */}
            <button
              onClick={() => setActiveTab('support')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'support'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Support Tickets
            </button>

            {/* Show loading state when determining subscription status */}
            {subscriptionLoading && (
              <div className="py-3 px-1">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && hasAnySubscriptionWithGeneral() && (
            <div className="text-gray-700">
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">General Settings</h2>
                  
                  {/* Company Logo Section */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Company Logo</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Upload your company logo to be used in PPE inspection reports and official documents. 
                      Recommended size: 300x100px. Maximum file size: 5MB.
                    </p>
                    
                    <div className="space-y-4">
                      {/* Current Logo Preview */}
                      {generalSettings.company_logo_preview && (
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Current Logo</label>
                          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 inline-block">
                            <img
                              src={generalSettings.company_logo_preview}
                              alt="Company Logo"
                              className="max-h-20 max-w-60 object-contain"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={removeCompanyLogo}
                            className="ml-3 px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                          >
                            Remove Logo
                          </button>
                        </div>
                      )}
                      
                      {/* Upload Section */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {generalSettings.company_logo_preview ? 'Replace Logo' : 'Upload Logo'}
                        </label>
                        <div className="mt-1 flex items-center space-x-4">
                          <label className="cursor-pointer bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoChange}
                              className="sr-only"
                            />
                            Choose File
                          </label>
                          <span className="text-sm text-gray-500">
                            {generalSettings.company_logo instanceof File ? generalSettings.company_logo.name : 'No file chosen'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                </div>
                
                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <button
                    onClick={saveGeneralSettings}
                    disabled={saving}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'subscriptions' && renderSubscriptionsTab()}
          {activeTab === 'ppe' && hasSubscription('ppe') && renderPpeTab()}
          {activeTab === 'support' && <SupportTicketsPanel />}

          {/* Show message when no subscriptions and trying to access restricted content */}
          {!hasAnySubscription() && activeTab !== 'subscriptions' && activeTab !== 'support' && (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-orange-800 mb-2">No Active Subscriptions</h3>
                  <p className="text-orange-600 text-sm">
                    This feature requires an active subscription. Please check the Subscriptions tab to view available options.
                  </p>
                </div>
                <button
                  onClick={() => setActiveTab('subscriptions')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  View Subscriptions
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;